import { emit, listen } from "@tauri-apps/api/event";
import { getCurrentOwnerEpoch } from "@/lib/owner-state-client";
import {
  getDesktopChatAuthScope,
  isCurrentDesktopChatAuthScope,
} from "./desktop-chat-auth-scope";
import { useSettingsFrameStore } from "./settings-frame-store";
import { requestNativeGlassSettled } from "./native-glass-settled";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";
import type { BrowserConnectionSession } from "./browser-connection-session";

const EVENT = "denker://browser-settings-yielded";
type Receipt = {
  sessionId: string;
  yieldId: string;
  displayId: number;
  ownerEpoch: number;
  authScope: unknown;
  ok: boolean;
};

/** The owner waits for the display that actually holds Settings to release its plate. */
export async function awaitBrowserSettingsYield(
  session: BrowserConnectionSession,
  commit: () => void,
  signal: AbortSignal,
): Promise<void> {
  const frame = useSettingsFrameStore.getState().frame;
  if (signal.aborted) throw new Error("Settings could not yield native focus.");
  if (!frame) {
    commit();
    return;
  }
  const epoch = getCurrentOwnerEpoch();
  let settled = false;
  let finish!: (ok: boolean) => void;
  const result = new Promise<void>((resolve, reject) => {
    finish = (ok) => {
      if (settled) return;
      settled = true;
      if (ok) resolve();
      else reject(new Error("Settings could not yield native focus."));
    };
  });
  const stop = () => finish(false);
  const timer = window.setTimeout(stop, 8500);
  signal.addEventListener("abort", stop, { once: true });
  const registration = listen<Receipt>(EVENT, ({ payload }) => {
    if (
      payload.sessionId === session.id &&
      payload.yieldId === session.yieldId &&
      payload.displayId === useSettingsFrameStore.getState().frame?.displayId &&
      frame.sessionId === useSettingsFrameStore.getState().frame?.sessionId &&
      payload.ownerEpoch === epoch &&
      epoch === getCurrentOwnerEpoch() &&
      isCurrentDesktopChatAuthScope(payload.authScope)
    )
      finish(payload.ok === true);
  });
  void registration.then(() => {
    if (!settled) {
      if (signal.aborted) stop();
      else commit();
    }
  }, stop);
  try {
    await result;
  } finally {
    window.clearTimeout(timer);
    signal.removeEventListener("abort", stop);
    void registration.then(safeTauriUnlisten).catch(() => {});
  }
}

export function acknowledgeBrowserSettingsYield(
  session: Pick<BrowserConnectionSession, "id" | "yieldId">,
  displayId: number,
): () => void {
  const signal = new AbortController();
  const epoch = getCurrentOwnerEpoch(),
    authScope = getDesktopChatAuthScope();
  void requestNativeGlassSettled(signal.signal)
    .then(
      () => true,
      () => false,
    )
    .then((ok) => {
      if (
        !signal.signal.aborted &&
        epoch === getCurrentOwnerEpoch() &&
        isCurrentDesktopChatAuthScope(authScope)
      )
        void emit(EVENT, {
          sessionId: session.id,
          yieldId: session.yieldId,
          displayId,
          ownerEpoch: epoch,
          authScope,
          ok,
        }).catch(() => {});
    });
  return () => signal.abort();
}
